import React, { useRef } from 'react';
import { Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { observer } from 'mobx-react';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import {
  Download,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { useStores } from '../../../stores';
import Table from '@HomaHealth/ui-component-library/lib/_components/molecules/Table';
import { Button } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import recommendationsInternalScreenPopUpMessages from '../../../core-utils/messages/recommendationsInternalScreenPopUpMessages';
import Snackbar from '../../atoms/Snackbar';
import properties from '../../../core-utils/properties';
import { downloadCSV } from '../../../core-utils/helper-utils/downloadPdfHelper';

export interface RecommendationsInternalScreenPopUpProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
}

const RecommendationsInternalScreenPopUp: React.FC<RecommendationsInternalScreenPopUpProps> = observer(
  ({ open, setOpen, title }) => {
    const useStyles = makeStyles()({
      hide: {
        display: 'none',
      },
      upperGrid: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '20px',
      },
      modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      paper: {
        width: '1106px',
        height: '600px',
        padding: '24px 28px 40px 32px',
        backgroundColor: patientAccessTheme.palette.status.white,
        border: 'none !important',
        position: 'relative',
      },
      button: {
        padding: '0px',
      },
      diseasesTableHeader: {
        height: '48px',
        backgroundColor: patientAccessTheme.palette.grey[400],
      },
      overallGridStyles: {
        width: '100%',
        marginRight: '150px',
      },
      ellipses: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        width: '300px',
        whiteSpace: 'nowrap',
        '&:hover': {
          overflow: 'visible',
          whiteSpace: 'normal',
          height: 'auto',
        },
      },
      downloadButtonGrid: {
        marginTop: '20px',
      },
      downloadCSVGrid: {
        marginRight: '10px',
      },
    });

    const { classes } = useStyles();

    const downloadAnchorRef = useRef<HTMLAnchorElement>(null);
    const messages = recommendationsInternalScreenPopUpMessages;
    const recommendedTabStore = useStores().recommendedTabStore;

    const recommendationsTableColumns =
      recommendedTabStore?.columnsInRecommendedTable;
    const recommendationsTableData =
      recommendedTabStore?.dataInRecommendedTable;
    const employerGroupStore = useStores().employerGroupStore;
    const [snackbarState, setSnackbarState] = React.useState<{
      open: boolean;
      message: string;
      severity: MuiColor | undefined;
      duration: null | number;
    }>({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });

    const handleClose = () => {
      setOpen(false);
    };

    const handleCloseSnackBar = () => {
      setSnackbarState({
        open: false,
        message: '',
        severity: undefined,
        duration: null,
      });
    };

    const handleOpenSnackBar = (
      message: string,
      severity: MuiColor,
      duration: number | null,
    ) => {
      setSnackbarState({
        open: true,
        message: message,
        severity: severity,
        duration: duration,
      });
    };

    const handleDownloadCSV = (
      downloadAnchorRef: React.RefObject<HTMLAnchorElement | null>,
    ) => {
      const url =
        properties.aiqBaseUrl +
        properties.recommendedScreen +
        properties.recommendedDownloadCsv;
      const requestBody = {
        eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
        category: recommendedTabStore.categoryData,
        additional_data: recommendedTabStore.additionalData,
      };

      downloadCSV(
        downloadAnchorRef,
        url,
        requestBody,
        recommendedTabStore.additionalData.replace('View ', ''),
      );
      handleOpenSnackBar(messages.downloadCSVMessage, 'info', 5000);
    };

    // const handleDownloadExcel = (
    //   downloadAnchorRef: React.RefObject<HTMLAnchorElement>,
    // ) => {
    //   const url =
    //     properties.aiqBaseUrl +
    //     properties.recommendedScreen +
    //     properties.recommendedDownloadExcel +
    //     '?eg_nid=' +
    //     employerGroupStore?.employerGroupResponseData?.eg_nid +
    //     '&category=' +
    //     recommendedTabStore.categoryData +
    //     '&additional_data=' +
    //     recommendedTabStore.additionalData;
    //   downloadExcel(downloadAnchorRef, url, recommendedTabStore.additionalData.replace("View ",""));
    //   handleOpenSnackBar(messages.downloadExcelMessage, 'info', 5000);
    // };

    return (
      <>
        <Modal open={open} onClose={handleClose} className={classes.modal}>
          <Paper className={classes.paper}>
            <Grid className={classes.upperGrid}>
              <PageTitleAndSubTitle title={title} subTitle="" />
              <SvgClose
                style={{ cursor: 'pointer' }}
                color={patientAccessTheme.palette.status.icon}
                onClick={handleClose}
              />
            </Grid>
            <Grid>
              <Table
                onHoverRowData={() => {}}
                classNameForTableHeaderRowTop={classes.diseasesTableHeader}
                columns={recommendationsTableColumns}
                data={recommendationsTableData}
                controlledPageIndex={0}
                overallGridStyles={classes.overallGridStyles}
                isRisingRiskPage={false}
                isManualPagination={true}
                tableHeight="400px"
                // classNameForTableDataRow={classes.ellipses}
              />
            </Grid>
            <Grid
              container
              direction="row"
              className={classes.downloadButtonGrid}
              sx={{
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
              }}
            >
              <a
                className={classes.hide}
                href="empty"
                ref={downloadAnchorRef}
              />
              <Grid className={classes.downloadCSVGrid}>
                <Button
                  label={messages.downloadCSV}
                  variant="outlined"
                  color="primary"
                  height="33px"
                  width="180px"
                  borderRadius="4px"
                  startIcon={<Download />}
                  onClick={() => handleDownloadCSV(downloadAnchorRef)}
                />
              </Grid>
              {/* Need to implement in future  so commenting now */}
              {/* <Grid item>
                <Button
                  label={messages.downloadExcel}
                  variant="outlined"
                  color="primary"
                  height="33px"
                  width="180px"
                  borderRadius="4px"
                  startIcon={<Download />}
                  onClick={() => handleDownloadExcel(downloadAnchorRef)}
                />
              </Grid> */}
            </Grid>
          </Paper>
        </Modal>
        <Snackbar
          {...snackbarState}
          handleClose={handleCloseSnackBar}
          vertical="bottom"
          horizontal="right"
        />
      </>
    );
  },
);
export default RecommendationsInternalScreenPopUp;
